import {
  ETIQUETA_ETAPA_MINEROS,
  ETIQUETA_ETAPA_MINEROS_EN,
  ETIQUETA_MOVIMIENTO_MINEROS,
  ETIQUETA_MOVIMIENTO_MINEROS_EN,
  ETIQUETA_NOVEDAD_MINEROS,
  ETIQUETA_NOVEDAD_MINEROS_EN,
  type BolsaDeMineros,
} from '@cnc/contracts';
import {et, tr, txt} from '../i18n/idioma';
import {fecha, numero, textoOGuion} from '../utilidades/formato';
import {AYUDA_MINEROS} from './ayuda-mineros';
import {type ColumnaDeTabla, type DefinicionDeColumnas} from './columnas';
import {G} from './columnas-inventario';

// Columnas de la tabla de Mineros (Inventario → Mineros), con los mismos
// grupos que la del Consolidado. Se ordena en la pantalla: `bruto` da el valor
// para ordenar (numero o texto).
export type ColumnaDeMineros = ColumnaDeTabla<BolsaDeMineros, string> & {
  bruto: (b: BolsaDeMineros) => string | number | null;
};

const ETAPA_ORDEN = ['planta', 'camino', 'bodega', 'puerto', 'navegando', 'llego', 'entregado'];
const conDatos = (b: BolsaDeMineros, v: number, decimales: number) => (b.completa ? numero(v, decimales) : '—');

// Nota de ayuda (la i del titulo): sin `ayuda`, sale de AYUDA_MINEROS por su id.
const columna = (
  id: string,
  titulo: ColumnaDeTabla<BolsaDeMineros>['titulo'],
  grupo: ColumnaDeTabla<BolsaDeMineros>['grupo'],
  valor: (b: BolsaDeMineros) => string,
  bruto: (b: BolsaDeMineros) => string | number | null,
  opciones: {porDefecto?: boolean; ancho?: number; numerica?: boolean; ayuda?: ColumnaDeMineros['ayuda']} = {},
): ColumnaDeMineros => ({id, titulo, grupo, valor, bruto, orden: id, ayuda: AYUDA_MINEROS[id], ...opciones});

export const COLUMNAS_DE_MINEROS: ColumnaDeMineros[] = [
  columna(
    'bbNo',
    'BB #',
    G.principal,
    b => b.bbNo,
    b => b.bbNo,
    {porDefecto: true, ancho: 130},
  ),
  columna(
    'tenor',
    txt('Tenor', 'Grade'),
    G.principal,
    b => (b.tenor === 'high' ? tr('Alto', 'High') : tr('Bajo', 'Low')),
    b => b.tenor,
    {porDefecto: true, ancho: 80},
  ),
  columna(
    'etapa',
    txt('Etapa', 'Stage'),
    G.principal,
    b =>
      b.etapa
        ? et(ETIQUETA_ETAPA_MINEROS, ETIQUETA_ETAPA_MINEROS_EN)[b.etapa] +
          (['planta', 'camino', 'bodega'].includes(b.etapa)
            ? b.asignada
              ? tr(' · asignado', ' · assigned')
              : tr(' · disponible', ' · available')
            : '')
        : tr('aún no existía', 'did not exist yet'),
    b => (b.etapa ? ETAPA_ORDEN.indexOf(b.etapa) * 2 + (b.asignada ? 1 : 0) : -1),
    {porDefecto: true, ancho: 200},
  ),
  columna(
    'exportacion',
    txt('Exportación', 'Export'),
    G.principal,
    b => textoOGuion(b.exportacion),
    b => b.exportacion,
    {
      porDefecto: true,
      ancho: 110,
    },
  ),
  columna(
    'contenedor',
    txt('Contenedor', 'Container'),
    G.principal,
    b => textoOGuion(b.contenedor),
    b => b.contenedor,
    {
      porDefecto: true,
      ancho: 130,
    },
  ),
  columna(
    'tm',
    'TM',
    G.principal,
    b => textoOGuion(b.tm),
    b => b.tm,
    {porDefecto: true, ancho: 80},
  ),
  columna(
    'lotes',
    txt('Lotes', 'Lots'),
    G.principal,
    b => `${b.lotesConDatos}/${b.lotes}${b.completa ? '' : ' ⚠'}`,
    b => b.lotesConDatos - b.lotes,
    {
      porDefecto: true,
      ancho: 80,
    },
  ),
  columna(
    'sello',
    txt('Sello', 'Seal'),
    G.principal,
    b => textoOGuion(b.sello),
    b => b.sello,
    {ancho: 150},
  ),
  columna(
    'envio',
    txt('Envío de planta', 'Plant shipment'),
    G.origen,
    b => textoOGuion(b.envio),
    b => b.envio,
    {ancho: 110},
  ),
  columna(
    'codigosDeLotes',
    txt('Códigos de lotes', 'Lot codes'),
    G.origen,
    b => textoOGuion(b.codigosDeLotes),
    b => b.codigosDeLotes,
    {
      ancho: 180,
    },
  ),
  columna(
    'movimiento',
    txt('Movimiento', 'Movement'),
    G.origen,
    b => (b.movimiento ? et(ETIQUETA_MOVIMIENTO_MINEROS, ETIQUETA_MOVIMIENTO_MINEROS_EN)[b.movimiento] : '—'),
    b => b.movimiento,
    {ancho: 170},
  ),
  columna(
    'salidaPlanta',
    txt('Salida El Bagre', 'Left El Bagre'),
    G.fechas,
    b => fecha(b.salidaPlanta),
    b => b.salidaPlanta,
    {
      porDefecto: true,
      ancho: 120,
    },
  ),
  // Fecha de la declaracion de valor: desde ella el Big Bag cuenta en la bodega.
  columna(
    'fechaTm',
    txt('Fecha TM', 'TM date'),
    G.fechas,
    b => fecha(b.fechaTm),
    b => b.fechaTm,
    {ancho: 110},
  ),
  columna(
    'cargue',
    txt('Cargue', 'Loading'),
    G.fechas,
    b => fecha(b.cargue),
    b => b.cargue,
    {ancho: 110},
  ),
  columna(
    'ingresoCartagena',
    txt('Ingreso Cartagena', 'Arrived Cartagena'),
    G.fechas,
    b => fecha(b.ingresoCartagena),
    b => b.ingresoCartagena,
    {porDefecto: true, ancho: 130},
  ),
  columna(
    'tulas',
    txt('Tulas', 'Sacks'),
    G.origen,
    b => (b.tulasEsperadas ? `${b.tulasRecibidas}/${b.tulasEsperadas}` : '—'),
    b => b.tulasRecibidas - b.tulasEsperadas,
    {ancho: 80},
  ),
  columna(
    'salidaBodega',
    txt('Salida bodega', 'Left warehouse'),
    G.fechas,
    b => fecha(b.salidaBodega),
    b => b.salidaBodega,
    {
      porDefecto: true,
      ancho: 120,
    },
  ),
  columna(
    'etd',
    'ETD',
    G.fechas,
    b => fecha(b.etd),
    b => b.etd,
    {porDefecto: true, ancho: 110},
  ),
  columna(
    'eta',
    'ETA',
    G.fechas,
    b => fecha(b.eta),
    b => b.eta,
    {ancho: 110},
  ),
  columna(
    'tonHumedas',
    txt('Ton. húmedas', 'Wet tons'),
    G.pesos,
    b => conDatos(b, b.tonHumedas, 3),
    b => b.tonHumedas,
    {
      numerica: true,
      ancho: 110,
    },
  ),
  columna(
    'tonSecas',
    txt('Ton. secas', 'Dry tons'),
    G.pesos,
    b => conDatos(b, b.tonSecas, 3),
    b => b.tonSecas,
    {
      porDefecto: true,
      numerica: true,
      ancho: 100,
    },
  ),
  columna(
    'auG',
    'Au (g)',
    G.pesos,
    b => conDatos(b, b.auG, 2),
    b => b.auG,
    {porDefecto: true, numerica: true, ancho: 100},
  ),
  columna(
    'auGDocumento',
    txt('Au documento (g)', 'Au document (g)'),
    G.pesos,
    b => (b.auGDocumento === null ? '—' : numero(b.auGDocumento, 2)),
    b => b.auGDocumento,
    {numerica: true, ancho: 130},
  ),
  columna(
    'auGt',
    'Au (g/t)',
    G.pesos,
    b => conDatos(b, b.auGt, 2),
    b => b.auGt,
    {porDefecto: true, numerica: true, ancho: 90},
  ),
  columna(
    'auOz',
    'Au (oz)',
    G.pesos,
    b => conDatos(b, b.auOz, 3),
    b => b.auOz,
    {porDefecto: true, numerica: true, ancho: 90},
  ),
  columna(
    'agG',
    'Ag (g)',
    G.pesos,
    b => conDatos(b, b.agG, 2),
    b => b.agG,
    {numerica: true, ancho: 100},
  ),
  columna(
    'agOz',
    'Ag (oz)',
    G.pesos,
    b => conDatos(b, b.agOz, 3),
    b => b.agOz,
    {numerica: true, ancho: 90},
  ),
  columna(
    'valorDeclaradoUsd',
    txt('Valor declarado (USD)', 'Declared value (USD)'),
    G.pesos,
    b => (b.valorDeclaradoUsd === null ? '—' : numero(b.valorDeclaradoUsd, 2)),
    b => b.valorDeclaradoUsd,
    {numerica: true, ancho: 150},
  ),
  columna(
    'novedades',
    txt('Novedades', 'Issues'),
    G.principal,
    b =>
      b.tiposDeNovedad.length
        ? b.tiposDeNovedad.map(t => et(ETIQUETA_NOVEDAD_MINEROS, ETIQUETA_NOVEDAD_MINEROS_EN)[t]).join(' · ')
        : '—',
    b => b.tiposDeNovedad.length,
    {porDefecto: true, ancho: 260},
  ),
];

export const DEFINICION_MINEROS: DefinicionDeColumnas<BolsaDeMineros, string> = {
  clave: 'cnc.mineros.columnas',
  columnas: COLUMNAS_DE_MINEROS,
  grupos: [G.principal, G.pesos, G.origen, G.fechas],
  fija: 'bbNo',
};

// Orden en la pantalla: los vacios al final, numeros como numeros, BB # natural.
export function ordenarMineros(filas: BolsaDeMineros[], orden: string, direccion: 'asc' | 'desc') {
  const col = COLUMNAS_DE_MINEROS.find(c => c.id === orden) ?? COLUMNAS_DE_MINEROS[0]!;
  const signo = direccion === 'asc' ? 1 : -1;
  return [...filas].sort((x, y) => {
    const a = col.bruto(x);
    const b = col.bruto(y);
    if (a === null || a === '') return b === null || b === '' ? 0 : 1;
    if (b === null || b === '') return -1;
    const r =
      typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), 'es', {numeric: true});
    return r * signo || x.bbNo.localeCompare(y.bbNo, 'es', {numeric: true});
  });
}
